import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Tabs,
  Tab,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person,
  Search,
  ShoppingCart,
  LocalPharmacy,
  Notifications,
  ExitToApp,
  LocationOn,
  Phone,
  DeliveryDining,
  Call,
  Message,
  CheckCircle,
  Share,
  ContentCopy,
  Done
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { useCart } from '../../contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { pharmacyService } from '../../services/pharmacyService';
import { orderService } from '../../services/orderService';
import { chatService } from '../../services/chatService';
import { locationService } from '../../services/locationService';
import ChatWindow from '../../components/ChatWindow';
import NotificationPanel from '../../components/NotificationPanel';
import Chat from '@mui/icons-material/Chat';
import healthTips from '../../data/healthTips';
import { KeyboardArrowLeft, KeyboardArrowRight, Favorite } from '@mui/icons-material';

const CustomerDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [nearbyPharmacies, setNearbyPharmacies] = useState([]);
  const [filteredPharmacies, setFilteredPharmacies] = useState([]);
  const [allPharmacies, setAllPharmacies] = useState([]);
  const [filteredAllPharmacies, setFilteredAllPharmacies] = useState([]);
  const [pharmacySearchQuery, setPharmacySearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [locationPermission, setLocationPermission] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [favoritePharmacies, setFavoritePharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [currentPharmacy, setCurrentPharmacy] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [referralPoints, setReferralPoints] = useState(0);
  const [codeCopied, setCodeCopied] = useState(false);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const { user, logout } = useAuth();
  const { notifications } = useSocket();
  const { getTotalItems } = useCart();
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Get user's location for nearby pharmacies
      let userLocation = null;
      try {
        const locationResult = await locationService.requestLocationPermission();
        if (locationResult.success) {
          userLocation = {
            latitude: locationResult.position.latitude,
            longitude: locationResult.position.longitude
          };
          setUserLocation(userLocation);
          setLocationPermission('granted');
          // Update user's location in the backend
          try {
            await locationService.updateLocation(
              userLocation.latitude, 
              userLocation.longitude
            );
          } catch (updateError) {
            console.warn('Failed to update location in backend:', updateError);
            // Only show error if it's not a throttling error
            if (!updateError.message.includes('throttled')) {
              console.warn('Location update failed:', updateError.message);
            }
            // Continue without updating backend location
          }
        } else {
          setLocationPermission('denied');
        }
      } catch (locationError) {
        // Handle different types of location errors
        if (locationError.message.includes('already in progress') || 
            locationError.message.includes('cooldown period')) {
          // Silently ignore these errors - they're expected
          console.log('Location request skipped:', locationError.message);
        } else {
          // Log other errors but don't spam the console
          console.warn('Could not get user location:', locationError.message);
        }
        setLocationPermission('denied');
      }

      // Import api from authService
      const api = (await import('../../services/authService')).default;
      
      const [pharmaciesData, allPharmaciesData, ordersData, favoritesData, conversationsData, referralData] = await Promise.all([
        userLocation 
          ? locationService.getNearbyPharmacies(userLocation.latitude, userLocation.longitude, 10)
          : locationService.getAllPharmaciesWithLocation(),
        locationService.getAllPharmaciesWithLocation(), // Load all pharmacies
        orderService.getMyOrders(),
        pharmacyService.getFavoritePharmacies().catch(() => []), // Fallback to empty array if endpoint doesn't exist
        chatService.getCustomerConversations().catch(() => []), // Get conversations
        api.get('/customer/referral-code').then(res => res.data).catch(() => ({ referralCode: null, referralPoints: 0 })) // Get referral info
      ]);
      setNearbyPharmacies(Array.isArray(pharmaciesData.pharmacies) ? pharmaciesData.pharmacies : []);
      setFilteredPharmacies(Array.isArray(pharmaciesData.pharmacies) ? pharmaciesData.pharmacies : []);
      setAllPharmacies(Array.isArray(allPharmaciesData.pharmacies) ? allPharmaciesData.pharmacies : []);
      setFilteredAllPharmacies(Array.isArray(allPharmaciesData.pharmacies) ? allPharmaciesData.pharmacies : []);
      console.log('Orders data from API:', ordersData);
      setAllOrders(Array.isArray(ordersData) ? ordersData : []);
      setRecentOrders(Array.isArray(ordersData) ? ordersData.slice(0, 3) : []);
      setConversations(Array.isArray(conversationsData) ? conversationsData : []);
      setReferralCode(referralData?.referralCode || null);
      setReferralPoints(referralData?.referralPoints || 0);
      
      // If favorites API doesn't exist, fallback to order history
      if (Array.isArray(favoritesData) && favoritesData.length > 0) {
        setFavoritePharmacies(favoritesData);
      } else {
        // Fallback: Get unique pharmacies from recent orders
        const uniquePharmacies = [...new Set(
          (ordersData || [])
            .map(order => order.pharmacy?.name || order.pharmacy)
            .filter(Boolean)
        )];
        setFavoritePharmacies(uniquePharmacies.slice(0, 5)); // Limit to 5 favorites
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array - only run once on mount

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-rotate health tips carousel
  useEffect(() => {
    const interval = setInterval(() => {
      handleNextTip();
    }, 30000); // Change tip every 30 seconds

    return () => clearInterval(interval);
  }, [currentTipIndex]);

  // Filter pharmacies based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredPharmacies(nearbyPharmacies);
    } else {
      const filtered = nearbyPharmacies.filter(pharmacy => {
        const name = pharmacy.pharmacyName || pharmacy.name || '';
        const address = pharmacy.address?.street || '';
        const city = pharmacy.address?.city || '';
        const searchLower = searchQuery.toLowerCase();
        
        return name.toLowerCase().includes(searchLower) ||
               address.toLowerCase().includes(searchLower) ||
               city.toLowerCase().includes(searchLower);
      });
      setFilteredPharmacies(filtered);
    }
  }, [searchQuery, nearbyPharmacies]);

  // Filter all pharmacies based on search query and location filter
  useEffect(() => {
    let filtered = allPharmacies;
    
    // Filter by search query
    if (pharmacySearchQuery.trim()) {
      const searchLower = pharmacySearchQuery.toLowerCase();
      filtered = filtered.filter(pharmacy => {
        const name = pharmacy.pharmacyName || pharmacy.name || '';
        const address = pharmacy.address?.street || '';
        const city = pharmacy.address?.city || '';
        
        return name.toLowerCase().includes(searchLower) ||
               address.toLowerCase().includes(searchLower) ||
               city.toLowerCase().includes(searchLower);
      });
    }
    
    // Filter by location
    if (locationFilter.trim()) {
      const locationLower = locationFilter.toLowerCase();
      filtered = filtered.filter(pharmacy => {
        const address = pharmacy.address?.street || '';
        const city = pharmacy.address?.city || '';
        const state = pharmacy.address?.state || '';
        
        return address.toLowerCase().includes(locationLower) ||
               city.toLowerCase().includes(locationLower) ||
               state.toLowerCase().includes(locationLower);
      });
    }
    
    setFilteredAllPharmacies(filtered);
  }, [pharmacySearchQuery, locationFilter, allPharmacies]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleNextTip = () => {
    setCurrentTipIndex((prevIndex) => (prevIndex + 1) % healthTips.length);
  };

  const handlePreviousTip = () => {
    setCurrentTipIndex((prevIndex) => (prevIndex - 1 + healthTips.length) % healthTips.length);
  };

  const goToTip = (index) => {
    setCurrentTipIndex(index);
  };

  const handlePharmacySearchChange = useCallback((e) => {
    console.log('Pharmacy search onChange:', e.target.value);
    setPharmacySearchQuery(e.target.value);
  }, []);

  const handleLocationFilterChange = useCallback((e) => {
    console.log('Location filter onChange:', e.target.value);
    setLocationFilter(e.target.value);
  }, []);

  const handleOrderClick = (order) => {
    console.log('Order data:', order);
    console.log('Order items:', order.items);
    setSelectedOrder(order);
    setOrderDetailsOpen(true);
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsOpen(false);
    setSelectedOrder(null);
  };

  const handleConfirmDelivery = async (orderId) => {
    try {
      setLoading(true);
      const response = await orderService.confirmDelivery(orderId);
      
      // Reload orders to update the status
      const ordersData = await orderService.getMyOrders();
      setAllOrders(Array.isArray(ordersData) ? ordersData : []);
      setRecentOrders(Array.isArray(ordersData) ? ordersData.slice(0, 3) : []);
      
      // Find the updated order from the reloaded data
      const updatedOrder = ordersData.find(order => order._id === orderId);
      
      // Update the selected order with the latest data
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
      }
      
      // Show appropriate message based on the response
      const successMessage = response?.message || (selectedOrder?.status === 'delivered' ? 'Receipt confirmed successfully!' : 'Delivery confirmed successfully!');
      
      // Wait a moment before closing the dialog to show the success message
      setTimeout(() => {
        setOrderDetailsOpen(false);
        setSelectedOrder(null);
      }, 1500);
      
      alert(successMessage);
    } catch (error) {
      console.error('Error confirming delivery:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to confirm delivery. Please try again.';
      alert(errorMessage);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Filter pharmacies locally and show results
      // The filtering is already handled by the useEffect that watches searchQuery
      // Just ensure we're on the nearby pharmacies tab
      setTabValue(0);
    }
  };

  // All pharmacies search and filter functions


  const handleOpenChat = async (pharmacy) => {
    try {
      setLoading(true);
      // Get or create conversation
      const conversation = await chatService.getOrCreateConversation(pharmacy._id || pharmacy.id);
      setCurrentConversation(conversation);
      setCurrentPharmacy(pharmacy);
      setChatOpen(true);
    } catch (error) {
      console.error('Error opening chat:', error);
      setError('Failed to open chat');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseChat = () => {
    setChatOpen(false);
    setCurrentConversation(null);
    setCurrentPharmacy(null);
    // Reload conversations to update unread counts
    loadConversations();
  };

  const loadConversations = async () => {
    try {
      setConversationsLoading(true);
      const data = await chatService.getCustomerConversations();
      setConversations(data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'preparing': return 'warning';
      case 'ready': return 'info';
      case 'assigned': return 'info';
      case 'picked-up': return 'info';
      case 'in-transit': return 'info';
      case 'delivered': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <Person sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'primary.main', fontWeight: 'bold' }}>
            Dashboard
          </Typography>
          <IconButton 
            color="inherit"
            onClick={async () => {
              try {
                const shareData = {
                  title: 'PD App - Pharmacy Delivery',
                  text: 'Check out PD App - Your trusted pharmacy delivery service!',
                  url: window.location.origin
                };
                
                if (navigator.share) {
                  await navigator.share(shareData);
                } else {
                  // Fallback for browsers that don't support Web Share API
                  await navigator.clipboard.writeText(window.location.origin);
                  alert('App link copied to clipboard!');
                }
              } catch (error) {
                console.error('Error sharing:', error);
              }
            }}
          >
            <Share />
          </IconButton>
          <IconButton color="inherit" onClick={() => navigate('/products')}>
            <Search />
          </IconButton>
          <IconButton color="inherit" onClick={() => navigate('/cart')}>
            <Badge badgeContent={getTotalItems()} color="error">
              <ShoppingCart />
            </Badge>
          </IconButton>
          <IconButton 
            color="inherit"
            onClick={() => setNotificationPanelOpen(true)}
          >
            <Badge badgeContent={notifications.length} color="error">
              <Notifications />
            </Badge>
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToApp />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: { xs: 1, sm: 3 }, px: { xs: 1, sm: 3 } }}>
        {/* Welcome Section */}
        <Box sx={{ mb: { xs: 2, sm: 4 } }}>
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Welcome back, {user?.firstName || 'Customer'}!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Find and order medicines from nearby pharmacies
          </Typography>
        </Box>

        {/* Health Tips Carousel */}
        <Card
          sx={{
            mb: { xs: 1, sm: 2 },
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: { xs: 1, sm: 2 },
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: { xs: 0.75, sm: 1.5 }, pb: { xs: 0.75, sm: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.5, sm: 1 } }}>
              <Favorite sx={{ mr: 0.5, fontSize: { xs: 14, sm: 20 } }} />
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                Health Tips
              </Typography>
            </Box>
            <Box sx={{ position: 'relative', minHeight: { xs: 45, sm: 80 } }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: { xs: 0.25, sm: 0.5 }, fontSize: { xs: '0.7rem', sm: '1rem' }, lineHeight: { xs: 1.2, sm: 1.4 } }}>
                {healthTips[currentTipIndex].title}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95, fontSize: { xs: '0.6rem', sm: '0.75rem' }, lineHeight: { xs: 1.2, sm: 1.4 } }}>
                {healthTips[currentTipIndex].body}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 0.75, sm: 1 } }}>
              {/* Navigation Arrows */}
              <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 } }}>
                <IconButton
                  size="small"
                  onClick={handlePreviousTip}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                    width: { xs: 20, sm: 28 },
                    height: { xs: 20, sm: 28 }
                  }}
                >
                  <KeyboardArrowLeft sx={{ fontSize: { xs: 12, sm: 16 } }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleNextTip}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                    width: { xs: 20, sm: 28 },
                    height: { xs: 20, sm: 28 }
                  }}
                >
                  <KeyboardArrowRight sx={{ fontSize: { xs: 12, sm: 16 } }} />
                </IconButton>
              </Box>
              
              {/* Dots Indicator */}
              <Box sx={{ display: 'flex', gap: { xs: 0.15, sm: 0.4 }, alignItems: 'center' }}>
                {healthTips.slice(0, 6).map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => goToTip(index)}
                    sx={{
                      width: { xs: 4, sm: 6 },
                      height: { xs: 4, sm: 6 },
                      borderRadius: '50%',
                      bgcolor: index === currentTipIndex ? 'white' : 'rgba(255,255,255,0.4)',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.6)'
                      }
                    }}
                  />
                ))}
                {healthTips.length > 6 && (
                  <Typography variant="caption" sx={{ ml: { xs: 0.2, sm: 0.4 }, opacity: 0.8, fontSize: { xs: '0.55rem', sm: '0.65rem' } }}>
                    +{healthTips.length - 6}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Search Bar */}
        <Box component="form" onSubmit={handleSearch} sx={{ mb: { xs: 2, sm: 4 } }}>
          <TextField
            fullWidth
            placeholder="Search for medicines or pharmacies..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            onFocus={(e) => {
              // Don't select all text on focus to allow normal typing
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ 
              bgcolor: 'white',
              '& .MuiInputBase-root': {
                height: { xs: '40px', sm: '56px' }
              }
            }}
          />
        </Box>

        {/* Quick Stats */}
        <Grid container spacing={{ xs: 0.5, sm: 3 }} sx={{ mb: { xs: 2, sm: 4 } }}>
          <Grid size={{ xs: 4, sm: 3 }}>
            <Card sx={{ height: { xs: 'auto', sm: 'auto' } }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
                  <ShoppingCart sx={{ 
                    fontSize: { xs: 20, sm: 40 }, 
                    color: 'primary.main', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 0.5, sm: 0 }
                  }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1.1rem', sm: '2rem' } }}>
                      {loading ? '...' : allOrders.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Orders</Box>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Total Orders</Box>
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 4, sm: 3 }}>
            <Card sx={{ height: { xs: 'auto', sm: 'auto' } }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
                  <LocationOn sx={{ 
                    fontSize: { xs: 20, sm: 40 }, 
                    color: 'secondary.main', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 0.5, sm: 0 }
                  }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1.1rem', sm: '2rem' } }}>
                      {loading ? '...' : filteredPharmacies.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Pharmacies</Box>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Nearby Pharmacies</Box>
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 4, sm: 3 }}>
            <Card sx={{ height: { xs: 'auto', sm: 'auto' } }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
                  <Person sx={{ 
                    fontSize: { xs: 20, sm: 40 }, 
                    color: 'warning.main', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 0.5, sm: 0 }
                  }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1.1rem', sm: '2rem' } }}>
                      {loading ? '...' : favoritePharmacies.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Favorites</Box>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Favorite Pharmacies</Box>
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Referral Section */}
        {referralCode && (
          <Card sx={{ mb: { xs: 1, sm: 3 }, bgcolor: 'primary.main', color: 'white' }}>
            <CardContent sx={{ p: { xs: 1, sm: 3 }, pb: { xs: 1, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: { xs: 0.75, sm: 2 } }}>
                <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' }, mb: { xs: 0.25, sm: 1 } }}>
                    Your Referral Code
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 }, flexWrap: 'wrap', mb: { xs: 0.25, sm: 1 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 1 } }}>
                      <Typography 
                        variant="h4" 
                        fontWeight="bold"
                        sx={{ 
                          fontFamily: 'monospace',
                          letterSpacing: { xs: 0.5, sm: 2 },
                          p: { xs: 0.25, sm: 1 },
                          bgcolor: 'rgba(255,255,255,0.2)',
                          borderRadius: 1,
                          fontSize: { xs: '0.9rem', sm: '1.5rem' }
                        }}
                      >
                        {referralCode}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(referralCode);
                            setCodeCopied(true);
                            setTimeout(() => setCodeCopied(false), 2000);
                          } catch (error) {
                            console.error('Failed to copy:', error);
                          }
                        }}
                        sx={{ 
                          color: 'white',
                          bgcolor: 'rgba(255,255,255,0.2)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                          p: { xs: 0.25, sm: 1 }
                        }}
                      >
                        {codeCopied ? <Done sx={{ fontSize: { xs: 14, sm: 20 } }} /> : <ContentCopy sx={{ fontSize: { xs: 14, sm: 20 } }} />}
                      </IconButton>
                    </Box>
                    <Typography variant="body1" sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                      <strong>{referralPoints}</strong> Points
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mt: { xs: 0.25, sm: 1 }, opacity: 0.9, fontSize: { xs: '0.7rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'block' } }}>
                    Share your code with friends to earn points when they make their first order!
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size={window.innerWidth < 600 ? "small" : "medium"}
                  sx={{ 
                    bgcolor: 'white', 
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    fontSize: { xs: '0.65rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 2.5 },
                    py: { xs: 0.25, sm: 1 },
                    minWidth: { xs: 'auto', sm: 120 }
                  }}
                  onClick={async () => {
                    try {
                      if (navigator.share) {
                        await navigator.share({
                          title: 'Join PD App!',
                          text: `Use my referral code ${referralCode} when you sign up!`,
                          url: window.location.origin
                        });
                      } else {
                        // Fallback: copy to clipboard
                        await navigator.clipboard.writeText(
                          `Join PD App! Use my referral code: ${referralCode}\n${window.location.origin}`
                        );
                        alert('Referral link copied to clipboard!');
                      }
                    } catch (error) {
                      console.error('Error sharing:', error);
                    }
                  }}
                >
                  Share
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Main Content Tabs */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  minWidth: { xs: 'auto', sm: 'auto' },
                  px: { xs: 0.5, sm: 2 },
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  textTransform: 'none',
                  fontWeight: 500,
                  minHeight: { xs: 40, sm: 48 }
                },
                '& .MuiTabs-scrollButtons': {
                  '&.Mui-disabled': {
                    opacity: 0.3
                  }
                }
              }}
            >
              {/* Products tab commented out - customers should chat with pharmacies instead */}
              {/* <Tab 
                label={
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    Products
                    <Badge 
                      badgeContent={products.length} 
                      color="primary"
                      sx={{ 
                        position: 'absolute',
                        top: -8,
                        right: -12,
                        '& .MuiBadge-badge': { 
                          fontSize: '0.75rem',
                          minWidth: '18px',
                          height: '18px'
                        }
                      }}
                    />
                  </Box>
                } 
              /> */}
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOn fontSize="small" />
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Nearby</Box>
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocalPharmacy fontSize="small" />
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>All Pharmacies</Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>All</Box>
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ShoppingCart fontSize="small" />
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>My Orders</Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>Orders</Box>
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Person fontSize="small" />
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Favorites</Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>Favs</Box>
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chat fontSize="small" />
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Conversations</Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>Chats</Box>
                  </Box>
                } 
              />
              <Tab label="Profile" />
            </Tabs>
          </Box>

          {/* Products TabPanel commented out - customers should chat with pharmacies instead */}
          {/* <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Available Products
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            ) : products.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No products found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Check back later for new products
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {products.map((product) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product._id || product.id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              {product.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {product.pharmacyId?.pharmacyName || 'Unknown Pharmacy'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {product.category} • {product.brand}
                            </Typography>
                          </Box>
                          <Chip
                            label={product.isAvailable ? 'Available' : 'Out of Stock'}
                            color={product.isAvailable ? 'success' : 'error'}
                            size="small"
                          />
                        </Box>
                        
                        <Typography variant="h6" color="primary.main" gutterBottom>
                          ₦{product.price?.nigeria?.toLocaleString() || '0'}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {product.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<ShoppingCart />}
                            disabled={!product.isAvailable}
                            onClick={() => handleAddToCart(product)}
                          >
                            Add to Cart
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleViewProductDetails(product)}
                          >
                            View Details
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel> */}

          {/* Nearby Pharmacies Tab - Now index 0 */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                {searchQuery ? `Search Results for "${searchQuery}"` : 'Pharmacies Near You'}
              </Typography>
              {searchQuery && (
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={() => setSearchQuery('')}
                >
                  Clear Search
                </Button>
              )}
            </Box>
            
            {/* Location Permission Status */}
            {locationPermission === 'denied' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Location access is required to show nearby pharmacies. Please enable location permissions in your browser settings.
                </Typography>
                <Button 
                  size="small" 
                  onClick={async () => {
                    try {
                      const result = await locationService.requestLocationPermission();
                      if (result.success) {
                        setLocationPermission('granted');
                        setUserLocation({
                          latitude: result.position.latitude,
                          longitude: result.position.longitude
                        });
                        // Reload data with location
                        loadData();
                      }
                    } catch (error) {
                      console.error('Location permission error:', error);
                    }
                  }}
                  sx={{ mt: 1 }}
                >
                  Try Again
                </Button>
              </Alert>
            )}
            
            {locationPermission === 'granted' && userLocation && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Showing pharmacies within 10km of your location
                </Typography>
              </Alert>
            )}
            {searchQuery && (
              <Typography variant="body2" color="primary.main" sx={{ mb: 2, fontStyle: 'italic' }}>
                💡 Chat with pharmacies to inquire about specific medicines and products
              </Typography>
            )}
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            ) : filteredPharmacies.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {searchQuery ? 'No pharmacies found matching your search' : 'No pharmacies found'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {searchQuery ? 'Try a different search term or browse all pharmacies' : 'Try searching for pharmacies in your area'}
                </Typography>
                {searchQuery && (
                  <Typography variant="body2" color="primary.main" sx={{ fontStyle: 'italic' }}>
                    💡 Tip: Chat with pharmacies to inquire about specific medicines and products
                  </Typography>
                )}
              </Box>
            ) : (
              <Grid container spacing={3}>
                {filteredPharmacies.map((pharmacy) => (
                  <Grid size={{ xs: 12, md: 6 }} key={pharmacy._id || pharmacy.id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              {pharmacy.pharmacyName || pharmacy.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {pharmacy.address?.street && pharmacy.address?.city 
                                ? `${pharmacy.address.street}, ${pharmacy.address.city}` 
                                : 'Address not available'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {pharmacy.distance ? `${pharmacy.distance}km away` : 'Distance unknown'} • Rating: {pharmacy.rating || 'N/A'}
                            </Typography>
                          </Box>
                          <Chip
                            label={pharmacy.isVerified ? 'Verified' : 'Unverified'}
                            color={pharmacy.isVerified ? 'success' : 'warning'}
                            size="small"
                          />
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Chat />}
                            onClick={() => handleOpenChat(pharmacy)}
                          >
                            Chat
                          </Button>
                          {/* Browse Products button removed - customers should chat with pharmacies instead */}
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Phone />}
                            href={`tel:${pharmacy.contactInfo?.phone || pharmacy.phone}`}
                          >
                            Call
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>

          {/* All Pharmacies Tab - Now index 1 */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                All Pharmacies
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredAllPharmacies.length} pharmacies found
              </Typography>
            </Box>
            
            {/* Search and Filter Controls */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                placeholder="Search pharmacies by name..."
                value={pharmacySearchQuery}
                onChange={handlePharmacySearchChange}
                onFocus={(e) => {
                  // Don't select all text on focus to allow normal typing
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  bgcolor: 'white',
                  '& .MuiInputBase-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
              <TextField
                fullWidth
                placeholder="Filter by location..."
                value={locationFilter}
                onChange={handleLocationFilterChange}
                onFocus={(e) => {
                  // Don't select all text on focus to allow normal typing
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  bgcolor: 'white',
                  '& .MuiInputBase-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            ) : filteredAllPharmacies.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LocalPharmacy sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {pharmacySearchQuery || locationFilter ? 'No pharmacies found' : 'No pharmacies available'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {pharmacySearchQuery || locationFilter 
                    ? 'Try adjusting your search criteria' 
                    : 'Pharmacies will appear here when they register'
                  }
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {filteredAllPharmacies.map((pharmacy) => (
                  <Grid size={{ xs: 12, md: 6 }} key={pharmacy._id || pharmacy.id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocalPharmacy color="primary" />
                            <Box>
                              <Typography variant="h6" fontWeight="bold">
                                {pharmacy.pharmacyName || pharmacy.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {pharmacy.address?.city || pharmacy.city || 'Location not specified'}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip 
                            label={pharmacy.isVerified ? 'Verified' : 'Unverified'} 
                            color={pharmacy.isVerified ? 'success' : 'default'} 
                            size="small" 
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {pharmacy.address?.street || 'Address not available'}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                          {pharmacy.contactInfo?.phone && (
                            <Chip 
                              icon={<Phone fontSize="small" />}
                              label={pharmacy.contactInfo.phone} 
                              size="small" 
                              variant="outlined" 
                            />
                          )}

                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button 
                            variant="outlined" 
                            size="small"
                            onClick={() => handleOpenChat(pharmacy)}
                            startIcon={<Chat />}
                          >
                            Chat
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>

          {/* My Orders Tab - Now index 2 */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                My Orders
              </Typography>
              {/* <Button variant="outlined" size="small" onClick={() => navigate('/customer/orders')}>
                View All
              </Button> */}
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            ) : recentOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No orders yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start shopping to see your orders here
                </Typography>
              </Box>
            ) : (
              <List>
                {recentOrders.map((order, index) => (
                  <React.Fragment key={order._id || order.id}>
                    <ListItem 
                      onClick={() => handleOrderClick(order)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { 
                          backgroundColor: 'action.hover' 
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <LocalPharmacy />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={order.pharmacy?.name || order.pharmacy}
                        secondary={`${order.items?.length || order.items} items • ₦${(order.totalAmount || order.amount)?.toLocaleString()} • ${new Date(order.createdAt || order.date).toLocaleDateString()}`}
                      />
                      <Chip
                        label={order.status?.replace('-', ' ')}
                        color={getStatusColor(order.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </ListItem>
                    {index < recentOrders.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </TabPanel>

          {/* Favorites Tab - Now index 3 */}
          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Favorite Pharmacies
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : favoritePharmacies.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LocalPharmacy sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No favorite pharmacies yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Start browsing pharmacies and add them to your favorites
                </Typography>
                <Button variant="contained" onClick={() => setTabValue(0)}>
                  Browse Pharmacies
                </Button>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {favoritePharmacies.map((pharmacyName, index) => (
                  <Grid size={{ xs: 12, md: 6 }} key={index}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              {pharmacyName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Based on your order history
                            </Typography>
                          </Box>
                          <Chip
                            label="Favorite"
                            color="success"
                            size="small"
                          />
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                          {/* Browse Products button removed - customers should chat with pharmacies instead */}
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Phone />}
                          >
                            Call
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>

          {/* Conversations Tab - Now index 4 */}
          <TabPanel value={tabValue} index={4}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                My Conversations
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={loadConversations}
                disabled={conversationsLoading}
                startIcon={conversationsLoading ? <CircularProgress size={16} /> : null}
              >
                {conversationsLoading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Box>

            {loading || conversationsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            ) : conversations.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No conversations yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start chatting with pharmacies to see your conversations here
                </Typography>
              </Box>
            ) : (
              <List>
                {conversations.map((conversation, index) => (
                  <React.Fragment key={conversation._id}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: conversation.conversationType === 'rider-customer' ? 'success.main' : 'primary.main' }}>
                          {conversation.conversationType === 'rider-customer' 
                            ? (conversation.riderId?.firstName?.charAt(0) || 'R')
                            : (conversation.pharmacyId?.pharmacyName?.charAt(0) || 'P')
                          }
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" component="span">
                              {conversation.conversationType === 'rider-customer'
                                ? `Rider: ${conversation.riderId?.firstName || ''} ${conversation.riderId?.lastName || ''}`.trim() || 'Delivery Rider'
                                : (conversation.pharmacyId?.pharmacyName || 'Pharmacy')
                              }
                            </Typography>
                            {conversation.unreadCountForCustomer > 0 && (
                              <Chip 
                                label={conversation.unreadCountForCustomer} 
                                color="error" 
                                size="small" 
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary" component="span" display="block">
                              {conversation.lastMessage || 'No messages yet'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="span" display="block">
                              {conversation.lastMessageAt 
                                ? new Date(conversation.lastMessageAt).toLocaleString()
                                : 'No messages yet'}
                            </Typography>
                          </>
                        }
                      />
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Chat />}
                        onClick={() => {
                          setCurrentConversation(conversation);
                          // Check if this is a rider-customer conversation
                          if (conversation.conversationType === 'rider-customer' || conversation.riderId) {
                            setCurrentPharmacy(conversation.riderId); // Pass rider as the other party
                          } else {
                            setCurrentPharmacy(conversation.pharmacyId);
                          }
                          setChatOpen(true);
                        }}
                      >
                        Open Chat
                      </Button>
                    </ListItem>
                    {index < conversations.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </TabPanel>

          {/* Profile Tab - Now index 5 */}
          <TabPanel value={tabValue} index={5}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Profile Information
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Personal Information
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body1">
                        {user?.firstName} {user?.lastName}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">
                        {user?.email}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body1">
                        {user?.profile?.phone || 'Not provided'}
                      </Typography>
                    </Box>
                    <Button variant="outlined" size="small">
                      Edit Profile
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Delivery Address
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body1">
                        {user?.profile?.address?.street || 'No address set'}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        City, State
                      </Typography>
                      <Typography variant="body1">
                        {user?.profile?.address?.city}, {user?.profile?.address?.state}
                      </Typography>
                    </Box>
                    <Button variant="outlined" size="small">
                      Update Address
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Card>


        {/* Chat Window */}
        {chatOpen && currentConversation && currentPharmacy && (
          <ChatWindow
            conversation={currentConversation}
            pharmacy={currentPharmacy}
            onClose={handleCloseChat}
            onOrderPlaced={() => {
              handleCloseChat();
              loadData();
            }}
          />
        )}

        {/* Notification Panel */}
        <NotificationPanel 
          open={notificationPanelOpen}
          onClose={() => setNotificationPanelOpen(false)}
        />

        {/* Order Details Dialog */}
        <Dialog 
          open={orderDetailsOpen} 
          onClose={handleCloseOrderDetails}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Order Details
            {selectedOrder && (
              <Typography variant="body2" color="text.secondary">
                Order #{selectedOrder.orderNumber || selectedOrder._id?.slice(-8)}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            {selectedOrder && (
              <Box>
                {/* Order Status */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    label={selectedOrder.status?.replace('-', ' ')}
                    color={getStatusColor(selectedOrder.status)}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>

                {/* Pharmacy Info */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Pharmacy
                  </Typography>
                  <Typography variant="body1">
                    {selectedOrder.pharmacy?.name || selectedOrder.pharmacy}
                  </Typography>
                  {selectedOrder.pharmacy?.address && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedOrder.pharmacy.address}
                    </Typography>
                  )}
                </Box>

                {/* Rider Info */}
                {selectedOrder.riderId ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Delivery Rider
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'success.main' }}>
                        <DeliveryDining />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {selectedOrder.riderId?.firstName} {selectedOrder.riderId?.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Delivery Rider
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Contact Options */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {selectedOrder.riderId?.phone && (
                        <>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Call />}
                            onClick={() => window.open(`tel:${selectedOrder.riderId.phone}`)}
                            sx={{ textTransform: 'none' }}
                          >
                            Call
                          </Button>

                        </>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Delivery Status
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'grey.400' }}>
                        <DeliveryDining />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" color="text.secondary">
                          No rider assigned yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Your order will be assigned to a rider soon
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Order Items */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Items ({selectedOrder.items?.length || selectedOrder.items || 0})
                  </Typography>
                  {selectedOrder.items && Array.isArray(selectedOrder.items) ? (
                    <List>
                      {selectedOrder.items.map((item, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={item.productId?.name || item.name || item.productName || 'Unknown Product'}
                            secondary={
                              <Box>
                                <Typography variant="body2" component="div">
                                  Quantity: {item.quantity}
                                </Typography>
                                <Typography variant="body2" component="div">
                                  Unit Price: ₦{(item.price || item.unitPrice)?.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                                  Total: ₦{((item.price || item.unitPrice) * item.quantity)?.toLocaleString()}
                                </Typography>
                                {item.productId?.description && (
                                  <Typography variant="caption" color="text.secondary" component="div">
                                    {item.productId.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {selectedOrder.items} items
                    </Typography>
                  )}
                </Box>

                {/* Order Summary */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Order Summary
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2">₦{(selectedOrder.subtotal || selectedOrder.totalAmount)?.toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Delivery Fee:</Typography>
                    <Typography variant="body2">₦{(selectedOrder.deliveryFee || 0)?.toLocaleString()}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Total:</Typography>
                    <Typography variant="h6">₦{(selectedOrder.totalAmount || selectedOrder.amount)?.toLocaleString()}</Typography>
                  </Box>
                </Box>

                {/* Order Dates */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Order Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Placed: {new Date(selectedOrder.createdAt || selectedOrder.date).toLocaleString()}
                  </Typography>
                  {selectedOrder.updatedAt && (
                    <Typography variant="body2" color="text.secondary">
                      Last Updated: {new Date(selectedOrder.updatedAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>

                {/* Delivery Address */}
                {selectedOrder.deliveryAddress && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Delivery Address
                    </Typography>
                    <Typography variant="body1">
                      {selectedOrder.deliveryAddress.street}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedOrder.deliveryAddress.city}, {selectedOrder.deliveryAddress.state}
                    </Typography>
                  </Box>
                )}

                {/* Payment Receipt */}
                {selectedOrder.paymentReceipt && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Payment Receipt
                    </Typography>
                    <Box
                      component="img"
                      src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001'}/${selectedOrder.paymentReceipt}`}
                      alt="Payment Receipt"
                      sx={{
                        width: '100%',
                        maxHeight: '400px',
                        objectFit: 'contain',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8
                        }
                      }}
                      onClick={() => {
                        window.open(`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001'}/${selectedOrder.paymentReceipt}`, '_blank');
                      }}
                      onError={(e) => {
                        console.error('Failed to load image:', e.target.src);
                        e.target.style.display = 'none';
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {selectedOrder && (
              <>
                {(selectedOrder.status === 'in-transit' || selectedOrder.status === 'picked-up' || selectedOrder.status === 'assigned' || selectedOrder.status === 'delivered') && selectedOrder.riderId && !selectedOrder.actualDelivery && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => handleConfirmDelivery(selectedOrder._id)}
                    disabled={loading}
                  >
                    {selectedOrder.status === 'delivered' ? 'Confirm Receipt' : 'Mark as Delivered'}
                  </Button>
                )}
                <Button onClick={handleCloseOrderDetails}>
                  Close
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default CustomerDashboard;
